// ============================================================
// A BURST'S MARKS (plan body-map H2): what plays round the struck body,
// beside its hits - each glance (a tracer whipping past the outline, or
// sparks off a hand) and, past six hits, the rattle (a quick star over each
// place) with its count. Drawn once when the burst arrives, each at its own
// delay (CSS, `animation-delay`: one shot each, nothing for the clock).
// With motion off only the count shows.
// ============================================================

import { memo } from 'react';
import { glanceMark, rattleMarks, type ParryMark, type TracerMark } from '../../../engine/plate/burst';
import type { PlateFrame } from '../../../engine/plate/frame';
import fx from '../../../styles/PlateFx.module.css';
import type { HitPlay } from '../usePlateHits';
import { timed } from './timing';
import { ringPoints } from './specials/primitives';

const round = (n: number) => Math.round(n);
const polygon = (points: readonly (readonly number[])[]) => points.map((p) => p.map(round).join(',')).join(' ');

/** A dodged shot's streak past the outline, at the plate's scale `k`. */
function Tracer({ m, at, glow, k }: { m: TracerMark; at: number; glow: string; k: number }) {
  const w = 9 * k;
  const points = [
    [m.x, m.y],
    [m.x + w, m.y],
    [m.x + m.dx + w, m.y + m.dy],
    [m.x + m.dx, m.y + m.dy],
  ];
  return (
    <polygon
      className={fx.tracer}
      points={polygon(points)}
      style={timed(at, 0, { '--dx': round(m.dx), '--dy': round(m.dy) })}
      filter={glow}
      data-glance="tracer"
    />
  );
}

/** A star of four (or eight) points round a centre: the parry's flash, the rattle's marks. */
const starPoints = (x: number, y: number, r: number, waist: number) =>
  ringPoints([x, y], 8, (j) => (j % 2 ? waist : r), -Math.PI / 2);

/** A parried blow's shards and star, at the plate's scale `k`. */
function Parry({ m, at, glow, k }: { m: ParryMark; at: number; glow: string; k: number }) {
  return (
    <g data-glance="parry" filter={glow}>
      {m.shards.map(({ a, l }, i) => {
        const tip = [m.x + Math.cos(a) * l, m.y + Math.sin(a) * l];
        const side = [tip[0] - Math.sin(a) * 7 * k, tip[1] + Math.cos(a) * 7 * k];
        const style = timed(at, 0, { '--sx': round(Math.cos(a) * 90 * k), '--sy': round((Math.sin(a) * 90 + 40) * k) });
        return (
          <polygon
            key={i}
            className={i % 3 ? fx.shard : fx.shardHot}
            points={polygon([[m.x, m.y], side, tip])}
            style={style}
          />
        );
      })}
      <polygon className={fx.flashStar} points={starPoints(m.x, m.y, 90 * k, 18 * k)} style={timed(at)} />
    </g>
  );
}

/** The burst's glances and rattle, round the struck body. Memoised: its props are fixed for a burst. */
export const BurstMarks = memo(function BurstMarks({
  u,
  play,
  frame,
  moving,
}: {
  u: string;
  play: HitPlay;
  frame: Pick<PlateFrame, 'box' | 'anchors' | 'k'>;
  moving: boolean;
}) {
  const { burst, id } = play;
  const glow = `url(#${u}-burstGlow)`;
  const rattle = burst.rattle && rattleMarks(burst.rattle.places, id, frame);
  return (
    <g key={id} pointerEvents="none" data-testid="plate-burst">
      <filter id={`${u}-burstGlow`} x="-50%" y="-50%" width="200%" height="200%">
        <feGaussianBlur stdDeviation={8 * frame.k} result="b" />
        <feMerge>
          <feMergeNode in="b" />
          <feMergeNode in="SourceGraphic" />
        </feMerge>
      </filter>
      {moving &&
        burst.glances.map((g, k) => {
          const m = glanceMark(g, k, id, burst.melee, frame);
          return m.kind === 'tracer' ? (
            <Tracer key={k} m={m} at={g.at} glow={glow} k={frame.k} />
          ) : (
            <Parry key={k} m={m} at={g.at} glow={glow} k={frame.k} />
          );
        })}
      {rattle && burst.rattle && (
        <g data-rattle={burst.rattle.places.length}>
          {moving &&
            rattle.stars.map((s, i) => (
              <polygon
                key={i}
                className={fx.rattleStar}
                points={starPoints(s.x, s.y, 75 * frame.k, 8 * frame.k)}
                style={timed(burst.rattle!.at + s.delay)}
                filter={glow}
              />
            ))}
          <text
            className={fx.rattleCount}
            x={rattle.count.x}
            y={rattle.count.y}
            fontSize={230 * frame.k}
            style={timed(burst.rattle.at)}
            filter={glow}
          >
            {`+${burst.rattle.places.length}`}
          </text>
        </g>
      )}
    </g>
  );
});
